/**
 * The buzz winner's spoken-answer control for auto-host voice mode (#91, D2/§5.3).
 *
 * Renders only when it is this player's turn to answer (they hold the buzzer in an
 * AUTO game). The answer field is ALWAYS an editable textbox: with a working mic it
 * fills live from speech and the player can still correct a mis-heard transcript;
 * without STT or with a denied/empty mic they simply type (§5.3). Either way it
 * submits the one `submit_spoken_answer` command — a single action (FR-9.5).
 */

import type { Command, GameState } from '@jeopardy/shared';
import { useAnswerCapture } from '../voice/use-answer-capture';
import type { SpeechRecognizer } from '../voice/speech-recognition';

export interface SpokenAnswerProps {
  state: GameState | null;
  playerId: string | null;
  send: (command: Command) => void;
  /** Injectable for tests; defaults to the shared browser recognizer. */
  recognizer?: SpeechRecognizer;
}

export function SpokenAnswer({
  state,
  playerId,
  send,
  recognizer,
}: SpokenAnswerProps): React.JSX.Element | null {
  const capture = useAnswerCapture({ state, playerId, send, recognizer });
  if (!capture.active) return null;

  const canSubmit = capture.transcript.trim().length > 0;
  // A live mic is running only when STT is supported and hasn't errored/been denied.
  const micLive = capture.supported && capture.error === null;

  return (
    <div className="spoken-answer" data-testid="spoken-answer">
      <p className="spoken-answer__status muted" data-testid="spoken-answer-status">
        {/* Decorative pulse mirroring the mic-live state the status text announces; it
            carries no new information, so it's hidden from AT to avoid double-announcing. */}
        {micLive && capture.listening && (
          <span
            className="spoken-answer__mic"
            data-testid="spoken-answer-mic"
            aria-hidden="true"
          />
        )}
        {capture.error
          ? 'Mic unavailable — type your answer instead.'
          : !capture.supported
            ? 'Speech input isn’t available here — type your answer.'
            : capture.listening
              ? 'Listening… say your answer (or edit the text).'
              : 'Say or type your answer, then submit.'}
      </p>
      {/* Always editable: the mic fills it live, and the player can correct a
          mis-heard transcript or type when there's no working mic (§5.3). */}
      <textarea
        aria-label="Your answer"
        data-testid="spoken-answer-input"
        value={capture.transcript}
        onChange={(e) => capture.setTranscript(e.target.value)}
        autoFocus
      />
      <div className="controls">
        <button
          type="button"
          className="btn btn--primary"
          data-testid="spoken-answer-submit"
          disabled={!canSubmit}
          onClick={() => capture.submit()}
        >
          Submit answer
        </button>
        {micLive && (
          <button
            type="button"
            className="btn"
            data-testid="spoken-answer-restart"
            onClick={() => capture.restart()}
          >
            Restart
          </button>
        )}
      </div>
    </div>
  );
}
